import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { projectToday } from '@d-orca/shared/client/activities';
import { SegmentedControl } from '@components/ui/SegmentedControl/SegmentedControl';
import type { ISchedulerTask } from '@interfaces/IScheduler';
import { useCanAccess } from '@permissions/useFeatures';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { selectDialogOpen } from '@store/selectors/dialog.selectors';
import { setActivityFilter, setIssueFilter } from '@store/slices/monitor.slice';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { ChevronDownIcon } from '@utils/icons';
import { CardListSkeleton } from '../../ActivitiesPage/CardList/CardListSkeleton';
import { useActivitiesFromSchedule } from '../../ActivitiesPage/hooks/useActivitiesFromSchedule';
import { useBootstrapSchedule } from '../../ActivitiesPage/hooks/useBootstrapSchedule';
import type { IListItem } from '../../ActivitiesPage/model/listItem';
import { IssueFormDialog } from '../../IssuesPage/Dialog/IssueFormDialog';
import {
  SchedulerCapabilitiesContext,
  useResolveSchedulerCapabilities,
} from '../../SchedulerPage/context/SchedulerCapabilities';
import { ActivityLadderContext, usePageActivityLadder } from '../../SchedulerPage/hooks/useActivityLadder';
import { useSchedulerSite } from '../../SchedulerPage/hooks/useSchedulerStore';
import { MonitorActivityCard, MonitorIssueCard } from './MonitorCards';
import {
  ACTIVITY_FILTERS,
  ACTIVITY_GROUPS,
  groupInto,
  ISSUE_FILTERS,
  ISSUE_GROUPS,
  itemDueGroup,
  type DueGroup,
} from './layers';
import { useItemDrawer } from './useItemDrawer';
import { useLayerData, type ILocatedKind } from './useLayerData';
import styles from './layers.module.css';

const NO_TASKS: ISchedulerTask[] = [];

interface IGroupSectionProps {
  group: DueGroup;
  count: number;
  children: ReactNode;
}

/** A due-date section — "Overdue (46)" — that folds its cards away. */
function GroupSection({ group, count, children }: IGroupSectionProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  return (
    <div>
      <button
        type="button"
        className={styles.groupHeader}
        aria-expanded={open}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        <span className={open ? styles.caret : `${styles.caret} ${styles.caretCollapsed}`} aria-hidden>
          <ChevronDownIcon size={14} />
        </span>
        {t(`monitor.layers.groups.${group}`)} ({count})
      </button>
      {open && <div className={styles.groupCards}>{children}</div>}
    </div>
  );
}

interface ILayerPillsProps<F extends string> {
  located: ILocatedKind<F>;
  filters: readonly F[];
  /** The kind's pill labels under `monitor.layers`. */
  labelKey: 'activityFilters' | 'issueFilters';
  onPick: (filter: F) => void;
}

/** A kind's filter pills, each with its count — "Overdue (46)". */
function LayerPills<F extends string>({ located, filters, labelKey, onPick }: ILayerPillsProps<F>) {
  const { t } = useTranslation();
  return (
    <SegmentedControl
      variant="wrap"
      value={located.filter}
      onChange={onPick}
      ariaLabel={t('monitor.layers.filterLabel')}
      options={filters.map((id) => ({
        id,
        label: t(`monitor.layers.${labelKey}.${id}`),
        badge: `(${located.counts[id]})`,
      }))}
    />
  );
}

/**
 * The rail in the Activities / Issues layer: the layer's located items (the
 * same ones the building's chips show) as Trusstor's main-page cards in
 * due-date sections. A card opens the A&I page's own drawer.
 */
export function LayerList() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { layer, siteId, activities, issues, tradeOf } = useLayerData();
  const kind = layer === 'issues' ? 'issue' : 'activity';
  const current = kind === 'issue' ? issues : activities;
  const tz = useAppSelector(selectSiteTz);
  const dialogOpen = useAppSelector(selectDialogOpen);
  const canManageIssues = useCanAccess('issues.manage');
  // The schedule on screen, loaded into the scheduler store as the Activities &
  // Issues page loads it, so an activity's menu (Mark done, Take back) and its
  // drawer read the same task — and Trusstor's word for it — as that page does.
  const { detail, scheduleNumber } = useActivitiesFromSchedule(siteId);
  useBootstrapSchedule(siteId, detail, scheduleNumber);
  const site = useSchedulerSite(siteId);
  const { store: ladder } = usePageActivityLadder(siteId, site?.tasks ?? NO_TASKS);
  // A scheduler root, as the Scheduler page and the task drawer are: the cards'
  // menus read what the user may do from it.
  const capabilities = useResolveSchedulerCapabilities();
  const { open, drawer } = useItemDrawer(siteId);
  const title = t(kind === 'issue' ? 'monitor.layers.issues' : 'monitor.layers.activities');

  let body: ReactNode;
  if (current.loading || !tz) body = <CardListSkeleton />;
  else if (current.items.length === 0) body = <p className={styles.empty}>{t('activities.empty')}</p>;
  else {
    const card = (item: IListItem) => {
      const shared = { item, siteId, tz, trade: tradeOf(item), canManageIssues, onOpen: open };
      const { activity, issue } = item.raw;
      return issue ? (
        <MonitorIssueCard key={item.key} {...shared} issue={issue} />
      ) : (
        <MonitorActivityCard key={item.key} {...shared} task={activity} scheduleNumber={scheduleNumber} />
      );
    };
    // Trusstor sections its "To Do" view and the issues list; a status pick on
    // activities is a flat list.
    if (kind === 'activity' && activities.filter !== 'all') {
      body = <div className={styles.groupCards}>{current.items.map(card)}</div>;
    } else {
      const today = projectToday(tz);
      body = groupInto(kind === 'activity' ? ACTIVITY_GROUPS : ISSUE_GROUPS, current.items, (item) =>
        itemDueGroup(item, today),
      ).map(({ key, items: groupItems }) => (
        <GroupSection key={key} group={key} count={groupItems.length}>
          {groupItems.map(card)}
        </GroupSection>
      ));
    }
  }

  return (
    <SchedulerCapabilitiesContext.Provider value={capabilities}>
      <ActivityLadderContext.Provider value={ladder}>
        <section className={styles.list} aria-label={title}>
          <div className={styles.listHead}>
            <h3 className={styles.listTitle}>
              {title} <span className={styles.listCount}>({current.located.length})</span>
            </h3>
            {kind === 'activity' ? (
              <LayerPills
                located={activities}
                filters={ACTIVITY_FILTERS}
                labelKey="activityFilters"
                onPick={(next) => dispatch(setActivityFilter(next))}
              />
            ) : (
              <LayerPills
                located={issues}
                filters={ISSUE_FILTERS}
                labelKey="issueFilters"
                onPick={(next) => dispatch(setIssueFilter(next))}
              />
            )}
          </div>
          <div className={styles.listBody}>{body}</div>
          {drawer}
        </section>
        {dialogOpen && <IssueFormDialog />}
      </ActivityLadderContext.Provider>
    </SchedulerCapabilitiesContext.Provider>
  );
}
